import {
  type FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  getTestDeliveryCount,
  sendTestMessageForAll,
  type TCreateMessageForAll,
} from 'api/messages'
import {
  createSurvey,
  getSurvey,
  updateSurvey,
  type TSurvey,
  type TSurveyResponseFormat,
  type TSurveyStatus,
} from 'api/surveys'
import { Alert } from 'components/Alert'
import { Button } from 'components/Button'
import { ConfirmModal } from 'components/ConfirmModal'
import { Input } from 'components/Input'
import { ManagementFormLayout } from 'components/ManagementFormLayout'
import { Textarea } from 'components/Textarea'
import {
  useOrganizationId,
  useUnsavedChangesGuard,
} from 'components/OrganizationScope/context'
import { routes } from 'constants/routes'
import { useOrgPath } from 'hooks/useOrgPath'
import { commonStrings, sendAllMessageStrings } from 'constants/strings'
import { useAuth } from 'contexts/AuthContext'
import { useCurrentUser } from 'hooks/useCurrentUser'
import { useLineDispatchRecovery } from 'hooks/useLineDispatchRecovery'
import { useMountedRef } from 'hooks/useMountedRef'
import { useTags } from 'hooks/useTags'
import { EContentSectionType } from 'types'
import { EOrganizationFeature } from 'types/enum'
import {
  canFetchOrganizationFeature,
  isOrganizationFeatureEnabled,
} from 'utils/organizationFeature'
import { buildSendOperationOwnerKey } from 'utils/organizationStorage'
import {
  claimSendOperationIdentity,
  clearSendOperationIdentity,
  clearSendOperationIdentityByIntent,
  createLineDispatchIntentHash,
  isSendOperationPayloadMismatch,
} from '../SendMessageForAll/sendOperationIdentity'
import {
  buildSurveyPayload,
  createEmptyQuestion,
  createEmptyReminder,
  getAllowedSurveyStatusOptions,
  hydrateSurveyBuilder,
  MAX_REMINDER_MESSAGE_LENGTH,
  MAX_SURVEY_RESPONSE_LIMIT,
  SURVEY_REMINDER_TIME_PATTERN,
  type TSurveyBuilderComboRule,
  type TSurveyBuilderQuestion,
  type TSurveyBuilderReminder,
} from './builder'
import { ComboRuleEditor } from './ComboRuleEditor'
import { getSurveyErrorMessage } from './errors'
import {
  checkboxClassName,
  FieldGroup,
  FieldLabel,
  RadioPillGroup,
  SectionCard,
  SingleTagPicker,
} from './fields'
import { SurveyPreviewModal } from './PreviewModal'
import { QuestionEditor } from './QuestionEditor'
import { ReminderEditor } from './ReminderEditor'

const RESPONSE_FORMAT = 'FORM' satisfies TSurveyResponseFormat
const MAX_QUESTIONS = 10
const MIN_CHOICES = 2
const MAX_CHOICES = 8

// Mirrors string-length semantics used by class-validator's @MaxLength.
const getValidatedTextLength = (value: string) => {
  const presentationSequenceCount =
    value.match(/[^\uFE0F\uFE0E][\uFE0F\uFE0E]/g)?.length ?? 0
  const surrogatePairCount =
    value.match(/[\uD800-\uDBFF][\uDC00-\uDFFF]/g)?.length ?? 0
  return value.length - presentationSequenceCount - surrogatePairCount
}

const validateBuilder = (
  name: string,
  reminderEnabled: boolean,
  reminders: TSurveyBuilderReminder[],
  questions: TSurveyBuilderQuestion[],
  responseLimit: number | null,
): string | null => {
  if (!name.trim()) {
    return 'アンケート名を入力してください。'
  }
  if (
    responseLimit !== null &&
    (!Number.isInteger(responseLimit) ||
      responseLimit < 1 ||
      responseLimit > MAX_SURVEY_RESPONSE_LIMIT)
  ) {
    return `回答回数は1〜${MAX_SURVEY_RESPONSE_LIMIT}回で設定してください。`
  }
  if (questions.length === 0 || questions.length > MAX_QUESTIONS) {
    return `設問は1〜${MAX_QUESTIONS}問で設定してください。`
  }

  for (const question of questions) {
    if (!question.title.trim()) {
      return 'すべての設問文を入力してください。'
    }
    if (question.type === 'SINGLE' || question.type === 'MULTI') {
      const choices = question.choices ?? []
      if (choices.length < MIN_CHOICES || choices.length > MAX_CHOICES) {
        return `選択肢は${MIN_CHOICES}〜${MAX_CHOICES}個で設定してください。`
      }
      if (choices.some((choice) => !choice.label.trim())) {
        return 'すべての選択肢を入力してください。'
      }
      const normalizedLabels = choices.map((choice) => choice.label.trim())
      if (new Set(normalizedLabels).size !== normalizedLabels.length) {
        return '同じ設問内で選択肢名を重複して設定できません。'
      }
      if (
        question.type === 'MULTI' &&
        question.maxSelections !== undefined &&
        (!Number.isInteger(question.maxSelections) ||
          question.maxSelections < 1 ||
          question.maxSelections > choices.length)
      ) {
        return '最大選択数を選択肢数以内で設定してください。'
      }
    }
  }

  const normalizedTitles = questions.map((question) => question.title.trim())
  if (new Set(normalizedTitles).size !== normalizedTitles.length) {
    return '同じアンケート内で設問文を重複して設定できません。'
  }

  if (!reminderEnabled) return null
  if (reminders.length === 0) {
    return 'リマインドを有効にする場合は送信設定を1件以上追加してください。'
  }
  if (
    reminders.some(
      (reminder) =>
        !Number.isInteger(reminder.days) ||
        reminder.days < 1 ||
        reminder.days > 365 ||
        !SURVEY_REMINDER_TIME_PATTERN.test(reminder.time ?? '') ||
        !reminder.message.trim() ||
        getValidatedTextLength(reminder.message) >
          MAX_REMINDER_MESSAGE_LENGTH,
    )
  ) {
    return 'リマインドの日数と文面を正しく入力してください。'
  }
  return null
}

export default function SurveyEdit() {
  const navigate = useNavigate()
  const orgPathFor = useOrgPath()
  const { surveyId } = useParams<{ surveyId: string }>()
  const isEdit = surveyId !== undefined
  const { data: currentUser } = useCurrentUser()
  // タグ管理がOFFでもアンケートは保存でき、保存済みのタグ設定はそのまま残す
  const canUseTags = isOrganizationFeatureEnabled(
    currentUser,
    EOrganizationFeature.TAG_MANAGEMENT,
  )
  const {
    tags,
    loading: tagsLoading,
    error: tagsError,
    refetch: refetchTags,
  } = useTags({
    enabled: canFetchOrganizationFeature(currentUser, EOrganizationFeature.TAG_MANAGEMENT),
  })
  const loadRequestSequence = useRef(0)
  const saveInFlight = useRef(false)
  const { user: authUser } = useAuth()
  // logoutでlocalStorageは消えないため、共有PCで前任者のsource keyを引き継がないよう
  // 保存済みidentityを利用者×組織単位に閉じる
  const organizationId = useOrganizationId()
  const sendOperationOwnerKey = authUser
    ? buildSendOperationOwnerKey(authUser.uid, organizationId)
    : ''
  const { recover: recoverLineDispatch, acknowledgeProducerResult } =
    useLineDispatchRecovery()

  const [name, setName] = useState('')
  const [status, setStatus] = useState<TSurveyStatus>('DRAFT')
  const [persistedStatus, setPersistedStatus] = useState<TSurveyStatus>('DRAFT')
  const [thanksMessage, setThanksMessage] = useState('')
  const [completionTagId, setCompletionTagId] = useState<number | ''>('')
  const [responseLimit, setResponseLimit] = useState<number | null>(null)
  const [reminderEnabled, setReminderEnabled] = useState(false)
  const [reminders, setReminders] = useState<TSurveyBuilderReminder[]>([])
  const [questions, setQuestions] = useState<TSurveyBuilderQuestion[]>(() => [
    createEmptyQuestion(),
  ])
  const [comboRules, setComboRules] = useState<TSurveyBuilderComboRule[]>([])
  const [saving, setSaving] = useState(false)
  const [imageUploading, setImageUploading] = useState(false)
  const [loading, setLoading] = useState(isEdit)
  const [loadedSurveyId, setLoadedSurveyId] = useState<number | null>(null)
  const [loadedVersion, setLoadedVersion] = useState<number | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [warning, setWarning] = useState<string | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [testConfirmOpen, setTestConfirmOpen] = useState(false)
  const [testConfirmCount, setTestConfirmCount] = useState<number | null>(null)
  const [isTestCountLoading, setIsTestCountLoading] = useState(false)
  const [isTestSending, setIsTestSending] = useState(false)
  const [testError, setTestError] = useState<string | null>(null)
  const [testNotice, setTestNotice] = useState<string | null>(null)
  const mountedRef = useMountedRef()

  const markFormSaved = useUnsavedChangesGuard(
    JSON.stringify({
      name,
      status,
      thanksMessage,
      completionTagId,
      responseLimit,
      reminderEnabled,
      reminders,
      questions,
      comboRules,
    }),
    imageUploading,
  )

  const applySurvey = useCallback((survey: TSurvey) => {
    if (survey.responseFormat !== RESPONSE_FORMAT) {
      throw new Error('この回答形式はPhase 1の管理画面では編集できません。')
    }
    const builder = hydrateSurveyBuilder(survey)
    setName(survey.name)
    setStatus(survey.status)
    setPersistedStatus(survey.status)
    setThanksMessage(survey.thanksMessage ?? '')
    setCompletionTagId(survey.completionTagId ?? '')
    setResponseLimit(survey.responseLimit ?? null)
    setReminderEnabled(survey.reminderEnabled)
    setReminders(builder.reminders)
    setQuestions(builder.questions)
    setComboRules(builder.comboRules)
    setLoadedSurveyId(survey.id)
    setLoadedVersion(survey.version)
    markFormSaved()
  }, [markFormSaved])

  const loadSurvey = useCallback(async () => {
    if (!isEdit) return
    const numericSurveyId = Number(surveyId)
    if (!Number.isInteger(numericSurveyId) || numericSurveyId <= 0) {
      setLoading(false)
      setLoadError('アンケートIDが正しくありません。')
      return
    }

    const requestSequence = ++loadRequestSequence.current
    setLoading(true)
    setLoadError(null)
    setSaveError(null)
    setWarning(null)

    try {
      const survey = await getSurvey(numericSurveyId)
      if (requestSequence !== loadRequestSequence.current) return
      if (survey.id !== numericSurveyId) {
        throw new Error('取得したアンケートIDが一致しません。')
      }
      applySurvey(survey)
    } catch (error: unknown) {
      if (requestSequence !== loadRequestSequence.current) return
      setLoadError(
        getSurveyErrorMessage(
          error,
          'アンケートの読み込みに失敗しました。',
        ),
      )
    } finally {
      if (requestSequence === loadRequestSequence.current) {
        setLoading(false)
      }
    }
  }, [applySurvey, isEdit, surveyId])

  useEffect(() => {
    void loadSurvey()
    return () => {
      loadRequestSequence.current += 1
    }
  }, [loadSurvey])

  const removeComboReferences = (
    questionClientId: string,
    choiceClientId?: string,
  ) => {
    const referencesCondition = (condition: {
      questionClientId: string
      choiceClientId: string
    }) =>
      condition.questionClientId === questionClientId &&
      (choiceClientId === undefined ||
        condition.choiceClientId === choiceClientId)
    if (
      !comboRules.some((rule) => rule.conditions.some(referencesCondition))
    ) {
      return
    }
    setComboRules((current) =>
      current
        .map((rule) => ({
          ...rule,
          conditions: rule.conditions.filter(
            (condition) => !referencesCondition(condition),
          ),
        }))
        // OFFでは組み合わせタグを直せず、空条件は serializeComboRules で保存できないので外す
        .filter((rule) => canUseTags || rule.conditions.length > 0),
    )
    setWarning(
      '削除またはタイプ変更した設問・選択肢を参照する組み合わせ条件を外しました。',
    )
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (saveInFlight.current || imageUploading) return
    setSaveError(null)
    setWarning(null)

    const validationError = validateBuilder(
      name,
      reminderEnabled,
      reminders,
      questions,
      responseLimit,
    )
    if (validationError) {
      setSaveError(validationError)
      return
    }

    try {
      const payload = buildSurveyPayload({
        name: name.trim(),
        responseFormat: RESPONSE_FORMAT,
        status,
        thanksMessage,
        completionTagId: completionTagId === '' ? null : completionTagId,
        responseLimit,
        reminderEnabled,
        reminders: reminderEnabled ? reminders : [],
        questions,
        comboRules,
      })

      saveInFlight.current = true
      setSaving(true)
      if (isEdit && surveyId) {
        if (loadedVersion === null) {
          throw new Error('アンケートの更新versionを取得できませんでした。')
        }
        const result = await updateSurvey(Number(surveyId), {
          ...payload,
          expectedVersion: loadedVersion,
        })
        if ((result.droppedComboRuleCount ?? 0) > 0) {
          if (result.id !== Number(surveyId)) {
            throw new Error('更新したアンケートIDが一致しません。')
          }
          applySurvey(result)
          setWarning(
            `組み合わせタグルール ${result.droppedComboRuleCount} 件が設問変更により削除されました。内容を確認してください。`,
          )
          return
        }
      } else {
        await createSurvey(payload)
      }
      // 保存中に組織を切り替えたら、元の組織の一覧へ連れ戻さない
      if (mountedRef.current) navigate(orgPathFor(routes.SURVEY_MANAGEMENT))
    } catch (error: unknown) {
      setSaveError(getSurveyErrorMessage(error, '保存に失敗しました。'))
    } finally {
      saveInFlight.current = false
      setSaving(false)
    }
  }

  // テスト配信は保存済みの公開中アンケートを送る（未保存の編集は反映されない）
  const canTestSend = isEdit && persistedStatus === 'ACTIVE'

  const handleOpenTestConfirm = async () => {
    if (!canTestSend || loadedSurveyId === null) return
    setTestError(null)
    setTestNotice(null)
    setIsTestCountLoading(true)
    try {
      const { count } = await getTestDeliveryCount()
      if (count === 0) {
        setTestError(sendAllMessageStrings.testSend.noTarget)
        return
      }
      setTestConfirmCount(count)
      setTestConfirmOpen(true)
    } catch {
      setTestError(sendAllMessageStrings.testSend.countLoadFailed)
    } finally {
      setIsTestCountLoading(false)
    }
  }

  const handleTestSend = async () => {
    if (loadedSurveyId === null) return
    setIsTestSending(true)
    setTestError(null)
    let claimedClientMessageId: string | null = null
    try {
      const sections: TCreateMessageForAll[] = [
        {
          // 同じアンケートの再試行でpayloadを安定させるため固定IDを使う
          id: `survey-test-${loadedSurveyId}`,
          type: EContentSectionType.SURVEY,
          surveyId: loadedSurveyId,
        },
      ]
      const intentHash = await createLineDispatchIntentHash({ sections })
      const identity = await claimSendOperationIdentity({
        kind: 'test',
        intentHash,
        sourceRevision: 1,
        ownerKey: sendOperationOwnerKey,
      })
      claimedClientMessageId = identity.clientMessageId

      const recoveryDecision = await recoverLineDispatch({
        sourceType: 'TEST',
        sourceKey: identity.sourceKey,
        sourceRevision: identity.sourceRevision,
        intentHash: identity.intentHash,
      })
      if (recoveryDecision === 'TERMINAL_FAILED') {
        clearSendOperationIdentity(
          'test',
          sendOperationOwnerKey,
          identity.clientMessageId,
        )
        throw new Error(sendAllMessageStrings.sendFailed)
      }
      if (recoveryDecision !== 'RECOVERED') {
        const result: unknown = await sendTestMessageForAll(
          { sections, clientMessageId: identity.clientMessageId },
          organizationId,
        )
        if (result === false) {
          throw new Error(sendAllMessageStrings.sendFailed)
        }
        await acknowledgeProducerResult({
          sourceType: 'TEST',
          sourceKey: identity.sourceKey,
          sourceRevision: identity.sourceRevision,
        })
      }

      clearSendOperationIdentityByIntent(
        'test',
        sendOperationOwnerKey,
        identity.intentHash,
      )
      clearSendOperationIdentity(
        'test',
        sendOperationOwnerKey,
        identity.clientMessageId,
      )
      setTestNotice(sendAllMessageStrings.testSend.success)
      setTestConfirmOpen(false)
      setTestConfirmCount(null)
    } catch (error: unknown) {
      // 別payloadが同じ送信IDを占有している409だけはprovider未送信が確定しているので
      // identityを解放し、次回新しいIDで送れるようにする
      if (
        claimedClientMessageId &&
        (isSendOperationPayloadMismatch(error) ||
          (error instanceof Error &&
            error.message === sendAllMessageStrings.sendSourceAlreadyUsed))
      ) {
        clearSendOperationIdentity(
          'test',
          sendOperationOwnerKey,
          claimedClientMessageId,
        )
      }
      // Alertはモーダルの背面に描画されるため、閉じてエラーを見せる
      setTestConfirmOpen(false)
      setTestConfirmCount(null)
      setTestError(
        getSurveyErrorMessage(error, sendAllMessageStrings.sendFailed),
      )
    } finally {
      setIsTestSending(false)
    }
  }

  const layoutProps = {
    kicker: isEdit ? 'survey edit' : 'survey create',
    title: isEdit ? 'アンケート編集' : 'アンケート作成',
    description: '設問と、回答に応じて友だちに付けるタグを設定します。',
    backTo: orgPathFor(routes.SURVEY_MANAGEMENT),
    maxWidthClassName: 'max-w-[960px]',
  }

  if (
    loading ||
    (isEdit && !loadError && loadedSurveyId !== Number(surveyId))
  ) {
    return (
      <ManagementFormLayout {...layoutProps}>
        <p role="status" className="text-sm text-muted">
          読み込み中...
        </p>
      </ManagementFormLayout>
    )
  }

  if (loadError) {
    return (
      <ManagementFormLayout {...layoutProps}>
        <Alert>{loadError}</Alert>
        <div className="flex gap-2">
          <Button type="button" onClick={() => void loadSurvey()}>
            再読み込み
          </Button>
        </div>
      </ManagementFormLayout>
    )
  }

  return (
    <ManagementFormLayout {...layoutProps}>
      <form
        data-testid="survey-form"
        className="grid gap-5"
        noValidate
        onSubmit={handleSubmit}
      >
        {saveError ? <Alert>{saveError}</Alert> : null}
        {canUseTags && tagsError ? (
          <div className="space-y-2">
            <Alert>{tagsError}</Alert>
            <Button
              type="button"
              variant="secondary"
              disabled={tagsLoading}
              onClick={() => void refetchTags()}
            >
              {tagsLoading ? 'タグを読み込み中...' : 'タグを再読み込み'}
            </Button>
          </div>
        ) : null}
        {warning ? (
          <p
            className="rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800"
            role="status"
          >
            {warning}
          </p>
        ) : null}

        <SectionCard
          title="基本設定"
          description="アンケート名と公開状態、回答完了時の動きを設定します。"
        >
          <FieldLabel label="アンケート名">
            <Input
              maxLength={255}
              placeholder="例：ご来店後アンケート"
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
            />
          </FieldLabel>

          <div className="grid gap-4 sm:grid-cols-2">
            <FieldGroup
              label="ステータス"
              hint="「公開中」にすると友だちが回答できるようになります。"
            >
              <RadioPillGroup
                name="survey-status"
                ariaLabel="ステータス"
                options={getAllowedSurveyStatusOptions(persistedStatus)}
                value={status}
                onChange={setStatus}
              />
            </FieldGroup>

            <FieldGroup label="回答形式">
              <span className="inline-flex w-fit items-center rounded-full bg-accent-soft px-3 py-1.5 text-xs font-semibold text-ink">
                フォーム回答
              </span>
            </FieldGroup>
          </div>

          <FieldGroup
            label="回答回数制限"
            hint="友だち1人あたりの完了回数です。未設定の場合は何度でも回答できます。"
          >
            <RadioPillGroup
              name="survey-response-limit"
              ariaLabel="回答回数制限"
              options={[
                { value: 'unlimited', label: '何度でも回答可能' },
                { value: 'limited', label: '回答回数を制限する' },
              ]}
              value={responseLimit === null ? 'unlimited' : 'limited'}
              onChange={(mode) => {
                if (mode === 'unlimited') {
                  setResponseLimit(null)
                  return
                }
                setResponseLimit((current) => (current === null ? 1 : current))
              }}
            />
            {responseLimit !== null ? (
              <label className="grid max-w-[200px] gap-1.5 text-xs font-semibold text-ink">
                <span>回答できる回数</span>
                <Input
                  type="number"
                  min={1}
                  max={MAX_SURVEY_RESPONSE_LIMIT}
                  aria-label="回答できる回数"
                  value={responseLimit}
                  onChange={(event) =>
                    setResponseLimit(
                      event.target.value === ''
                        ? 0
                        : Number(event.target.value),
                    )
                  }
                />
              </label>
            ) : null}
          </FieldGroup>

          <FieldLabel
            label="サンクスメッセージ"
            hint="回答完了後の画面に表示されます。未入力の場合は定型文を表示します。"
          >
            <Textarea
              maxLength={2000}
              className="min-h-[88px]"
              placeholder="例：ご回答ありがとうございました！"
              value={thanksMessage}
              onChange={(event) => setThanksMessage(event.target.value)}
            />
          </FieldLabel>

          {canUseTags && (
            <FieldGroup
              label="完了タグ"
              hint="最後まで回答した友だちに自動で付けるタグです。"
            >
              <SingleTagPicker
                tags={tags}
                value={completionTagId}
                onChange={setCompletionTagId}
                ariaLabel="完了タグ"
              />
            </FieldGroup>
          )}
        </SectionCard>

        <QuestionEditor
          questions={questions}
          setQuestions={setQuestions}
          tags={tags}
          showTags={canUseTags}
          onRemoveComboReferences={removeComboReferences}
          onUploadingChange={setImageUploading}
        />

        {canUseTags && (
          <ComboRuleEditor
            comboRules={comboRules}
            setComboRules={setComboRules}
            questions={questions}
            tags={tags}
          />
        )}

        <SectionCard
          title="リマインド"
          description="未回答の友だちへ、配信からの経過日数に応じてLINEで再通知します。"
        >
          <label className="flex w-fit items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              className={checkboxClassName}
              checked={reminderEnabled}
              onChange={(event) => {
                const enabled = event.target.checked
                setReminderEnabled(enabled)
                if (enabled && reminders.length === 0) {
                  setReminders([createEmptyReminder()])
                }
              }}
            />
            <span>リマインドを有効にする</span>
          </label>
          {reminderEnabled ? (
            <ReminderEditor
              reminders={reminders}
              setReminders={setReminders}
            />
          ) : null}
        </SectionCard>

        {testError ? <Alert>{testError}</Alert> : null}
        {testNotice ? (
          <p
            className="rounded-2xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-800"
            role="status"
          >
            {testNotice}
          </p>
        ) : null}

        <div className="sticky bottom-0 -mx-1 flex flex-wrap items-center justify-end gap-3 rounded-2xl border border-border/70 bg-white/95 px-4 py-3 shadow-soft backdrop-blur-sm">
          <div className="mr-auto flex flex-wrap items-center gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => setPreviewOpen(true)}
            >
              プレビュー
            </Button>
            {isEdit ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  disabled={!canTestSend || isTestCountLoading || isTestSending}
                  onClick={() => void handleOpenTestConfirm()}
                >
                  {isTestCountLoading
                    ? '対象を確認中...'
                    : sendAllMessageStrings.testSend.button}
                </Button>
                <span className="text-xs text-muted">
                  {canTestSend
                    ? '保存済みの内容でテストユーザーに配信されます。'
                    : 'テスト配信は公開中のアンケートのみ利用できます。'}
                </span>
              </>
            ) : null}
          </div>
          <Button
            type="button"
            variant="outline"
            disabled={saving}
            onClick={() => navigate(orgPathFor(routes.SURVEY_MANAGEMENT))}
          >
            キャンセル
          </Button>
          <Button
            type="submit"
            disabled={
              saving ||
              (canUseTags && (tagsLoading || Boolean(tagsError))) ||
              imageUploading
            }
          >
            {saving ? '保存中...' : imageUploading ? '画像をアップロード中...' : '保存'}
          </Button>
        </div>
      </form>

      <SurveyPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        questions={questions}
      />

      <ConfirmModal
        textTitle={sendAllMessageStrings.testSend.title}
        textMessage={sendAllMessageStrings.testSend.content.replace(
          '${count}',
          (testConfirmCount ?? 0).toLocaleString('ja-JP'),
        )}
        textCancel={commonStrings.cancel}
        textConfirm={commonStrings.send}
        onClose={() => {
          setTestConfirmOpen(false)
          setTestConfirmCount(null)
        }}
        onConfirm={() => void handleTestSend()}
        open={testConfirmOpen}
        isLoading={isTestSending}
      />
    </ManagementFormLayout>
  )
}
